<script setup lang="ts">
import {computed, inject, ref, toRaw} from 'vue';
import {apps, LegacyOrder, legacyOrderCommands, legacyOrderKey, useEnums, usePhrases} from '@hantera/portal-app';
import {
  ContextMenuButton,
  DataColumnValuesEnumValue,
  DropdownItem,
  EnumsBar,
  IconReload,
  Spinner,
} from '@hantera/design-system';
import useBackend from '@/composition/useBackend';
import AppSlotView from '@/AppSlotView.vue';
import SaveButton from './SaveButton.vue';
import Decimal from "decimal.js";

const { legacyOrders: orders, user, legacyTagDefinitions: tagDefinitions } = useBackend();

const order = inject(legacyOrderKey, ref<LegacyOrder>(<any>{}));
const phrase = usePhrases();
const enums = useEnums()

function printJson() {
  console.log(toRaw(order.value));
}

function rebalancePayments() {
  orders.buildCommandGroup(
    order.value.orderId,
    'Rebalance payments', {}
  )
    .command(legacyOrderCommands.rebalancePayments, {})
    .send();
}

const canCancel = computed(() => !order.value.deliveries.find(d => d.state !== 'Open' && d.state !== 'Cancelled') && order.value.state !== 'Cancelled');

function addTag(value: DataColumnValuesEnumValue) {
  orders.buildCommandGroup(
    order.value.orderId,
    'Add tag {tagName}', {
    tagNumber: new Decimal(value.key),
    tagName: value.label,
  })
    .command(legacyOrderCommands.addOrderTag, {
      tagNumber: new Decimal(value.key)
    })
    .command(legacyOrderCommands.addActivityLog, {
      message: 'Added tag {tagName}',
      fields: {
        tagName: value.label
      }
    })
    .send();
}

function removeTag(value: DataColumnValuesEnumValue) {
  
  const tag = order.value.tags.find(r => r.tagNumber.eq(value.key))!
  
  orders.buildCommandGroup(
    order.value.orderId,
    'Remove tag {tagName}', {
    tagNumber: tag.tagNumber,
    tagName: value.label,
  })
    .command(legacyOrderCommands.removeOrderTag, {
      tagNumber: tag.tagNumber,
      actionId: tag.actionId && tag.actionId !== '00000000-0000-0000-0000-000000000000' ? tag.actionId : undefined
    })
    .command(legacyOrderCommands.addActivityLog, {
      message: 'Removed tag {tagName}',
      fields: {
        tagName: value.label
      }
    })
    .send();
}

function cancelOrder() {
  orders.buildCommandGroup(
    order.value.orderId,
    'Cancel order', {}
  )
    .command(legacyOrderCommands.cancelOrder, {})
    .command(legacyOrderCommands.addActivityLog, {
      message: 'Cancelled order',
      fields: {}
    })
    .send();
}

async function legacyRefresh() {
  try {
    await orders.legacyRefresh(order.value.orderId);
  }
  catch (e) {
    alert(e);
  }
}

const isLoading = computed(() => {
  return orders.isLoading.has(order.value.orderId) && (orders.isLoading.get(order.value.orderId) ?? 0) > 0;
});

const availableTags = computed(() => {
  
  const legacyTagKeys = new Set<string>(tagDefinitions.value.orderTags.map(r => r.number.toString()))

  return enums.valueSets('order', 'tags').value
    .map(set => {

      return {
        ...set,
        values: set.values
          .filter(v => legacyTagKeys.has(v.key))
      }
    })
})

const selectedTags = computed(() => {
  return order.value.tags.map(r => r.tagNumber.toString())
})

</script>

<template>
  <div class="legacy-order-header">
    <div class="tags">
      <EnumsBar
        :available-sets="availableTags"
        :selected-values="selectedTags"
        @add="addTag"
        @remove="removeTag"
      >
<!--        <template #dropdown>-->
<!--          <DropdownItem @click="() => enums.openEditor('order', 'tags')">-->
<!--            Configure Tags-->
<!--          </DropdownItem>-->
<!--        </template>-->
      </EnumsBar>
    </div>
    
    <div class="actions">
      <Spinner class="legacy-order-header__spinner" :style="!isLoading ? 'visibility: hidden' : ''" />
      <button
        class="legacy-order-header__legacy-refresh"
        data-sentry-unmask
        @click="legacyRefresh"
        title="Refresh"
      >
        <IconReload />
      </button>
      <div class="legacy-order-header__save">
        <SaveButton />
      </div>
      <div class="legacy-order-header__context-menu">
        <ContextMenuButton class="legacy-order-header__context-menu-button">
          <DropdownItem
            @click="printJson"
          >Print JSON to console</DropdownItem>
          <DropdownItem @click="rebalancePayments">Rebalance payments</DropdownItem>
          <DropdownItem
            @click="cancelOrder"
            v-if="canCancel"
          >{{ phrase('Cancel order') }}</DropdownItem>
          <AppSlotView :slot="apps.legacySlots.legacyOrder.contextMenu" />
        </ContextMenuButton>
      </div>
    </div>
  </div>
</template>

<style scoped>

.actions {
  display: flex;
  gap: 5px;
}

.tags {
  flex-grow: 1;
  align-items: center;
}

</style>

<style>
.legacy-order-header {
  display: flex;
  flex-grow: 1;
  gap: 5px;
}

.legacy-order-header__save {
  display: flex;
  justify-content: end;
}

.legacy-order-header__context-menu-button {
  height: 32px;
  width: 32px;
}

.legacy-order-header__legacy-refresh {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 32px;
  width: 32px;
  padding: 0;
  line-height: 0px;
  user-select: none;
}

.legacy-order-header__legacy-refresh svg {
  width: 17px;
}

.legacy-order-header__spinner {
  width: 22px;
  height: 22px;
  margin: 5px;
  vertical-align: middle;
}
</style>